import { useMutation } from '@tanstack/react-query';
import { useMessagingResource } from './use-messaging-resource';
import { ConversationKey } from '../../models';
import { useBroadcast } from '../../../common/broadcast-channel/use-broadcast';

export const useMarkConversationAsRead = ({
    conversationKey,
    onError,
    oldestUnreadMessageId,
    newestMessageId,
}: {
    conversationKey?: ConversationKey;
    onError: () => void;
    oldestUnreadMessageId?: number;
    newestMessageId?: number;
}) => {
    const messagingResource = useMessagingResource();

    const { postMessage } = useBroadcast('mark-conversation-as-read');

    type MutateParams = { conversationKey: ConversationKey };

    const mutation = useMutation<true, unknown, MutateParams, unknown>({
        // The mutationKey is composed of the selected conversation key and the oldest unread message id. This means
        // that the REST request to mark this conversation read due to the user seeing a particular oldest unread
        // message will only be executed once, after which the cached response is used.
        mutationKey: [
            'mark-conversation-as-read',
            conversationKey?.toString(),
            oldestUnreadMessageId,
        ],
        mutationFn: (variables) => {
            const { conversationKey } = variables;
            return messagingResource.markConversationAsRead(conversationKey);
        },
        onSuccess: (data, variables) => {
            const { conversationKey } = variables;
            postMessage({
                type: 'MARK_CONVERSATION_AS_READ',
                payload: {
                    conversationId: conversationKey.conversationId,
                    newestMessageId,
                },
            });
        },
        onError: () => {
            onError();
        },
    });

    const markConversationAsReadMutation = () => {
        if (conversationKey !== undefined && oldestUnreadMessageId !== undefined) {
            mutation.mutate({ conversationKey });
        }
    };

    return markConversationAsReadMutation;
};
